import { IAccount } from "@interfaces";
import { isPlaceholderName } from "./signupOfferConfig";

export interface ISignupNames {
    first_name: string;
    last_name: string;
}

/**
 * Supplies the names a new social account should be upgraded under.
 *
 * Resolves to null when there are none to be had — the visitor dismissed the
 * prompt, or the provider would not say.
 */
export type ResolveSignupNames = () => Promise<ISignupNames | null>;

const isRealName = (value?: string | null): boolean => !!value && value.trim() !== "";

/** Whether the account already carries a name of the visitor's own. */
export const hasRealName = (account?: IAccount | null): boolean =>
    isRealName(account?.first_name) &&
    isRealName(account?.last_name) &&
    !isPlaceholderName("first_name", account?.first_name) &&
    !isPlaceholderName("last_name", account?.last_name);

const isUsable = (names?: ISignupNames | null): boolean =>
    isRealName(names?.first_name) && isRealName(names?.last_name);

/**
 * The names to upgrade a new social account with, or null to leave it alone.
 *
 * The backend mints the points agent's branch identity (e.g.
 * `ft-DWF-guesttraveller`) during the upgrade, from the name the account holds at
 * that moment, and never revises it. Neither provider reliably gives us one:
 * Apple sends a name only in the first authorisation's form POST, which goes to
 * the backend and not to us, and the account comes back nameless.
 *
 * So rather than upgrade under a stand-in and file the visitor permanently as
 * "Guest Traveller", the upgrade is skipped when no real name can be had. That
 * costs points on this booking and nothing else — the account still works, and
 * the traveller form's "Save and Earn Points" remains a route to the upgrade once
 * real details exist.
 */
export const resolveUpgradeNames = async (
    account?: IAccount | null,
    resolve?: ResolveSignupNames,
): Promise<ISignupNames | null> => {
    if (hasRealName(account)) {
        return {
            first_name: (account as IAccount).first_name!.trim(),
            last_name: (account as IAccount).last_name!.trim(),
        };
    }

    if (!resolve) return null;

    try {
        const names = await resolve();
        if (!isUsable(names)) return null;
        return {
            first_name: (names as ISignupNames).first_name.trim(),
            last_name: (names as ISignupNames).last_name.trim(),
        };
    } catch (error) {
        // A provider lookup that fails is the same as one that says nothing.
        console.error("Could not resolve a name for the points upgrade:", error);
        return null;
    }
};
